import { getIsActive } from '@core/functions/get-is-active';
import {
    U2bColumnDefinitionBrakePoint,
    U2bColumnPipeName,
    U2bTableData,
    U2bTableTextAlignments,
} from '@core/components/layout/table/table.types';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';
import { BcmEmployee, EmployeePosition } from '@shared/models/bcm-employee';
import { FilterFieldType } from '@core/datafilter/available-filter-fields';
import {
    FILTER_OPERATORS_DEFAULT_DATE,
    FILTER_OPERATORS_DEFAULT_NUMERIC,
    FILTER_OPERATORS_DEFAULT_SELECT,
    FILTER_OPERATORS_DEFAULT_STRING_ALT,
} from '@core/datafilter/constants';
import { TranslationService } from '@core/translation/translation.service';

const employeePositionTranslationKeys: Record<EmployeePosition, string> = {
    department_head: 'bcmEmployees.position.department_head',
    deputy_head: 'bcmEmployees.position.deputy_head',
    employee: 'bcmEmployees.position.employee',
};

const employeePositionSelectOptions: EmployeePosition[] = ['department_head', 'deputy_head', 'employee'];

const employeeActiveStatusFilterTranslationKeys = {
    true: 'bcmEmployees.table.active',
    false: 'bcmEmployees.table.inactive',
    all: 'bcmEmployees.filters.all',
};

const employeeNumericColumnAlignment = {
    [U2bColumnDefinitionBrakePoint.Desktop]: U2bTableTextAlignments.Right,
    [U2bColumnDefinitionBrakePoint.Tablet]: U2bTableTextAlignments.Right,
    [U2bColumnDefinitionBrakePoint.Mobile]: U2bTableTextAlignments.Right,
};

function translateEmployeePosition(position: EmployeePosition | null | undefined): string {
    if (!position) {
        return '';
    }

    return TranslationService.translate(employeePositionTranslationKeys[position]);
}

function translateEmployeeActiveStatus(isActive: boolean | undefined): string {
    return TranslationService.translate(isActive ? 'bcmEmployees.table.active' : 'bcmEmployees.table.inactive');
}

export const employeesTableData: U2bTableData = {
    tableId: BcmUserTableSettingKey.Employees,
    hasServerSideFiltering: true,
    columnDefinitions: [
        {
            property: 'profileImage',
            name: 'Avatar',
            hideNameInHeader: true,
            isActive: getIsActive(true),
            isAvatar: true,
            isU2bAvatar: true,
            avatarAttribute: 'profileImage',
            parseItem: (item: BcmEmployee) => item.profileImage ?? item.profile?.image ?? null,
            fixedWidth: '60',
            exportKey: null,
            disableSort: true,
        },
        {
            property: 'profile.displayName',
            name: 'Name',
            isActive: getIsActive(true),
            translationKey: 'bcmEmployees.field.name',
            exportKey: 'profile.displayName',
            parseItem: (item: BcmEmployee) => item.displayName,
            filter: {
                property: 'profile.displayName',
            },
        },
        {
            property: 'employee_number',
            name: 'Personalnummer',
            isActive: getIsActive(true),
            translationKey: 'bcmEmployees.field.employeeNumber',
            exportKey: 'employee_number',
            parseItem: (item: BcmEmployee) => item.employeeNumber,
            filter: {},
        },
        {
            property: 'profile.email',
            name: 'E-Mail',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.email',
            exportKey: 'profile.email',
            parseItem: (item: BcmEmployee) => item.profile?.email ?? '',
            filter: {
                property: 'profile.email',
            },
        },
        {
            property: 'profile.contactPhone',
            name: 'Telefon',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.phone',
            exportKey: 'profile.contactPhone',
            parseItem: (item: BcmEmployee) => item.profileContactPhoneSummary,
            filter: {
                property: 'profile.contactPhone',
            },
        },
        {
            property: 'profile.address',
            name: 'Adresse',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.address',
            exportKey: 'profile.address',
            parseItem: (item: BcmEmployee) => item.profileAddressSummary,
            filter: {
                property: 'profile.address',
            },
        },
        {
            property: 'position',
            name: 'Position',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.position',
            exportKey: 'positionLabel',
            parseItem: (item: BcmEmployee) => translateEmployeePosition(item.position),
            filter: {
                fieldType: FilterFieldType.Select,
                operators: FILTER_OPERATORS_DEFAULT_SELECT,
                selectOptions: employeePositionSelectOptions,
                selectOptionsTranslations: employeePositionTranslationKeys,
            },
        },
        {
            property: 'entry_date',
            name: 'Eintrittsdatum',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.entryDate',
            pipe: U2bColumnPipeName.Date,
            exportKey: 'entry_date',
            parseItem: (item: BcmEmployee) => item.entryDate,
            filter: {
                fieldType: FilterFieldType.Date,
                operators: FILTER_OPERATORS_DEFAULT_DATE,
            },
        },
        {
            property: 'exit_date',
            name: 'Austrittsdatum',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.exitDate',
            pipe: U2bColumnPipeName.Date,
            exportKey: 'exit_date',
            parseItem: (item: BcmEmployee) => item.exitDate,
            filter: {
                fieldType: FilterFieldType.Date,
                operators: FILTER_OPERATORS_DEFAULT_DATE,
            },
        },
        {
            property: 'primaryDepartment',
            name: 'Abteilung',
            isActive: getIsActive(true),
            translationKey: 'bcmEmployees.field.primaryDepartment',
            exportKey: 'primaryDepartment.name',
            sortProperty: 'primaryDepartment.name',
            parseItem: (item: BcmEmployee) => item.departmentSummary,
            filter: {
                property: 'primaryDepartment.name',
            },
        },
        {
            property: 'additionalDepartments',
            name: 'Abteilungs Freigaben',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.additionalDepartments',
            exportKey: 'additionalDepartments.name',
            disableSort: true,
            parseItem: (item: BcmEmployee) => item.additionalDepartmentsSummary,
            filter: {
                property: 'additionalDepartments.name',
            },
        },
        {
            property: 'workingTimeModel',
            name: 'Arbeitszeitmodell',
            isActive: getIsActive(true),
            translationKey: 'bcmEmployees.field.workingTimeModel',
            exportKey: 'workingTimeModel.name',
            sortProperty: 'workingTimeModel.name',
            parseItem: (item: BcmEmployee) => item.workingTimeModelSummary,
            filter: {
                property: 'workingTimeModel.name',
            },
        },
        {
            property: 'internal_hourly_rate',
            name: 'Interner Stundensatz',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.internalHourlyRate',
            pipe: U2bColumnPipeName.Currency,
            exportKey: 'internal_hourly_rate',
            parseItem: (item: BcmEmployee) => item.internalHourlyRate,
            alignment: employeeNumericColumnAlignment,
            filter: {
                fieldType: FilterFieldType.Numeric,
                operators: FILTER_OPERATORS_DEFAULT_NUMERIC,
            },
        },
        {
            property: 'annual_vacation_days',
            name: 'Jahresurlaubstage',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.annualVacationDays',
            exportKey: 'annual_vacation_days',
            parseItem: (item: BcmEmployee) => item.annualVacationDays,
            alignment: employeeNumericColumnAlignment,
            filter: {
                fieldType: FilterFieldType.Numeric,
                operators: FILTER_OPERATORS_DEFAULT_NUMERIC,
            },
        },
        {
            property: 'vacation_days_taken',
            name: 'Genommene Urlaubstage',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.vacationDaysTaken',
            exportKey: 'vacation_days_taken',
            disableSort: true,
            parseItem: (item: BcmEmployee) => item.vacationDaysTaken,
            alignment: employeeNumericColumnAlignment,
            filter: {
                fieldType: FilterFieldType.Numeric,
                operators: FILTER_OPERATORS_DEFAULT_NUMERIC,
            },
        },
        {
            property: 'vacation_days_remaining',
            name: 'Resturlaub',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.vacationDaysRemaining',
            exportKey: 'vacation_days_remaining',
            disableSort: true,
            parseItem: (item: BcmEmployee) => item.vacationDaysRemainingSummary,
            alignment: employeeNumericColumnAlignment,
            filter: {
                fieldType: FilterFieldType.Numeric,
                operators: FILTER_OPERATORS_DEFAULT_NUMERIC,
            },
        },
        {
            property: 'business_email',
            name: 'E-Mail-Adresse',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.businessEmail',
            exportKey: 'business_email',
            parseItem: (item: BcmEmployee) => item.businessEmail ?? '',
            filter: {},
        },
        {
            property: 'business_landline_phone',
            name: 'Festnetznummer',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.businessLandlinePhone',
            exportKey: 'business_landline_phone',
            parseItem: (item: BcmEmployee) => item.businessLandlinePhone ?? '',
            filter: {},
        },
        {
            property: 'business_mobile_phone',
            name: 'Dienstliche Mobilnummer',
            isActive: getIsActive(false, false, false),
            translationKey: 'bcmEmployees.field.businessMobilePhone',
            exportKey: 'business_mobile_phone',
            parseItem: (item: BcmEmployee) => item.businessMobilePhone ?? '',
            filter: {},
        },
        {
            property: 'isActive',
            name: 'Status',
            translationKey: 'bcmEmployees.table.status',
            isActive: getIsActive(false, false, false),
            exportKey: 'isActiveLabel',
            parseItem: (item: BcmEmployee) => translateEmployeeActiveStatus(item.isActive),
            filter: {
                fieldType: FilterFieldType.Boolean,
                selectOptionsTranslations: employeeActiveStatusFilterTranslationKeys,
            },
        },
    ],
};
